import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import { InputField } from "../../Shared/InputField"
import Spinner from '../../Shared/Spinner'
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const CollectorBalanceSummary = () => {
    const [cash_balances, setCashBalance] = useState([]);
    const [title, setTitle] = useState('Collector Balance Summary');
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const [errors, setErrors] = useState([])
    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
    })

    const {fdate, tdate} = formData

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (location.state?.fdate && location.state?.tdate) {
            setFormData(prev => ({
                ...prev,
                fdate: location.state.fdate,
                tdate: location.state.tdate,
            }))
            fetchData({ fdate: location.state.fdate, tdate: location.state.tdate })
            setTitle(`Collector Balance Summary From ${location.state.fdate} To ${location.state.tdate}`)
        } else {
            fetchData({fdate: fdate, tdate: tdate})
        }
    }, [])

    const [columnDefs] = useState([
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Collector",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Account",
            field: "account_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "AccountNumber",
            field: "account_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Opening",
            field: "opening_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Credit",
            field: "credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ]);

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`collector-accounts/balance-summary?fdate=${payload.fdate}&tdate=${payload.tdate}`)
            setCashBalance(response?.data);
            setIsLoading(false);

            const output = response?.data.reduce((data, row) => {
                data.opening_balance += parseFloat(row.opening_balance);
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                return data;
            }, {opening_balance: 0.00, debit: 0.00, credit: 0.00, balance: 0.00});
            setPinnedBottomRowData([
                {
                    opening_balance: parseFloat(output.opening_balance).toFixed(2),
                    debit: parseFloat(output.debit).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                    balance: ((output.opening_balance + output.debit) - output.credit).toFixed(2)
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error.response.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error.response.data)
            }
        }
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
        
    }

    const onSubmit = (e) => {
        e.preventDefault()
        
        setTitle(`Collector Balance Summary From ${fdate} To ${tdate}`)
        const payload = {fdate, tdate}
        fetchData(payload)
    }

    const onDoubleClick = (payload) => {
        navigate('/collector-account-balance-detail', { state: {payload: payload}})
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-4">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{ title }</h3>
            </div>
            <div className="card-body">
            <div className="row">
                <div className="col-md-12">
                    <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return '';
                                if (e.data.debit < 1 && e.data.credit < 1) return '';
                                
                                onDoubleClick({
                                    account_id: e.data.account_id,
                                    fdate: fdate,
                                    tdate: tdate,
                                })
                            }}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            suppressMenuHide={true}
                            
                            rowData={cash_balances}
                            domLayout={"normal"}
                            rowSelection={"multiple"}
                        
                            pagination={false}
                            onGridReady={onGridReady}
                        
                            resizable={true}
                            animateRows={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                        
                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                </div>
            </div>
        </div>
        </div>
      <br/>
    </>
  )
}

export default CollectorBalanceSummary
